import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { defaultSiteContent } from './content/defaultSiteContent';
import { themeVars } from './content/theme';
import { getContent, type Litter } from './lib/api';
import type { SiteContent as SiteContentDoc } from './content/types';
import { HomePage } from './pages/HomePage';
import { ApplicationPage } from './pages/ApplicationPage';
import { LitterDetailPage } from './pages/LitterDetailPage';
import { AdminLayout } from './pages/admin/AdminLayout';
import { Overview } from './pages/admin/Overview';
import { Inbox } from './pages/admin/Inbox';
import { Applications } from './pages/admin/Applications';
import { Litters } from './pages/admin/Litters';
import { SiteContent } from './pages/admin/SiteContent';
import { FormBuilder } from './pages/admin/FormBuilder';
import { Newsletter } from './pages/admin/Newsletter';
import { Settings } from './pages/admin/Settings';
import { ThemeEditorApp } from './pages/editor/ThemeEditor';

// Rendered inside the theme editor's <iframe>. Gets its own real viewport (so mobile
// media queries fire), receives draft content live via postMessage, and reports clicks.
function EditorPreview() {
  const [content, setContent] = useState<SiteContentDoc>(defaultSiteContent);
  const [litters, setLitters] = useState<Litter[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [inspector, setInspector] = useState(true);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data.type === 'ed:content') { setContent(data.content); if (data.litters) setLitters(data.litters); }
      if (data.type === 'ed:selected') setSelected(data.section ?? null);
      if (data.type === 'ed:inspector') setInspector(Boolean(data.enabled));
    };
    window.addEventListener('message', onMessage);
    window.parent.postMessage({ type: 'ed:ready' }, window.location.origin);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    root.querySelectorAll('[data-ed-section]').forEach((el) => {
      const id = el.getAttribute('data-ed-section');
      el.classList.toggle('ed-hl-selected', selected === id);
      el.classList.toggle('ed-hl-hover', hovered === id && selected !== id);
    });
  }, [selected, hovered, content]);

  const onClick = (event: MouseEvent) => {
    if (!inspector) return;
    event.preventDefault(); event.stopPropagation();
    const target = event.target as HTMLElement;
    const sectionEl = target.closest('[data-ed-section]');
    if (sectionEl) setSelected(sectionEl.getAttribute('data-ed-section'));
    const imageEl = target.closest('[data-ed-image]');
    if (imageEl) {
      const bounds = imageEl.getBoundingClientRect();
      const aspectRatio = bounds.width > 0 && bounds.height > 0 ? bounds.width / bounds.height : undefined;
      window.parent.postMessage({ type: 'ed:image', target: imageEl.getAttribute('data-ed-image'), aspectRatio }, window.location.origin);
      return;
    }
    const fieldEl = target.closest('[data-ed-field]');
    if (fieldEl) window.parent.postMessage({ type: 'ed:field', field: fieldEl.getAttribute('data-ed-field') }, window.location.origin);
    else if (sectionEl) window.parent.postMessage({ type: 'ed:select', section: sectionEl.getAttribute('data-ed-section') }, window.location.origin);
  };
  const onOver = (event: MouseEvent) => {
    if (!inspector) return;
    const el = (event.target as HTMLElement).closest('[data-ed-section]');
    setHovered(el?.getAttribute('data-ed-section') ?? null);
  };

  return (
    <div ref={rootRef} className={`ed-preview-root ${inspector ? 'ed-inspector-on' : ''}`} style={themeVars(content)} onClickCapture={onClick} onMouseOver={onOver} onMouseLeave={() => setHovered(null)}>
      <HomePage content={content} litters={litters} />
    </div>
  );
}

export function App() {
  const [litters, setLitters] = useState<Litter[]>([]);
  const [content, setContent] = useState(defaultSiteContent);
  const isEditorPreview = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('__ed');

  useEffect(() => {
    if (isEditorPreview) return;
    const controller = new AbortController();
    getContent(controller.signal).then((result) => {
      setContent(result.content);
      setLitters(result.litters);
    }).catch(() => undefined);
    return () => controller.abort();
  }, [isEditorPreview]);

  if (isEditorPreview) return <EditorPreview />;

  return (
    <Routes>
      <Route path="/" element={<div style={themeVars(content)}><HomePage content={content} litters={litters} /></div>} />
      <Route path="/apply" element={<ApplicationPage />} />
      <Route path="/admin/design" element={<ThemeEditorApp />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<Overview />} />
        <Route path="inbox" element={<Inbox />} />
        <Route path="applications" element={<Applications />} />
        <Route path="litters" element={<Litters />} />
        <Route path="content" element={<SiteContent />} />
        <Route path="form" element={<FormBuilder />} />
        <Route path="newsletter" element={<Newsletter />} />
        <Route path="settings" element={<Settings />} />
      </Route>
      <Route path="/litters/:id" element={<div style={themeVars(content)}><LitterDetailPage content={content} litters={litters} /></div>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
